import React, { useState, useEffect } from 'react';
import { FileText, AlertCircle, CheckCircle } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { findReferenceInText, formatReferenceContent } from '../../utils/referenceExtractor';

export const SectionModal = ({
    isOpen,
    onClose,
    sectionReference,
    sectionContent,
    fileName,
    extractedText
}) => {
    const [foundContent, setFoundContent] = useState(null);
    const [isSearching, setIsSearching] = useState(false);

    // Search for reference in extracted text when modal opens
    useEffect(() => {
        if (isOpen && sectionReference && extractedText) {
            setIsSearching(true);
            
            // Small delay to show searching state
            setTimeout(() => {
                const result = findReferenceInText(sectionReference, extractedText);
                setFoundContent(result);
                setIsSearching(false);
                console.log('Section Search Result:', result);
            }, 100);
        } else {
            setFoundContent(null);
        }
    }, [isOpen, sectionReference, extractedText]);

    // Use found content or fallback to provided sectionContent
    const displayContent = foundContent?.found ? foundContent.content : sectionContent;
    const hasContent = !!(displayContent || foundContent?.context);
    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Section Reference"
            size="lg"
        >
            <div className="space-y-6">
                {/* File Info */}
                {fileName && (
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                        <FileText className="h-4 w-4" />
                        <span>{fileName}</span>
                    </div>
                )}

                {/* Reference Badge */}
                <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg border border-slate-100">
                    <span className="text-sm font-medium text-slate-700">Reference:</span>
                    <Badge variant="primary" className="text-sm px-3 py-1">
                        {sectionReference}
                    </Badge>
                    {foundContent?.found && (
                        <Badge variant="success" className="text-xs ml-auto">
                            <CheckCircle className="h-3 w-3 mr-1 inline" />
                            Found in Document
                        </Badge>
                    )}
                </div>

                {/* Content */}
                <div className="rounded-xl border border-slate-200 p-6 bg-white shadow-inner">
                    {isSearching ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mb-4"></div>
                            <p className="text-slate-600">Searching document...</p>
                        </div>
                    ) : hasContent ? (
                        <div className="prose prose-slate max-w-none">
                            <div className="pl-4 border-l-4 border-primary-500">
                                <h4 className="text-lg font-semibold text-slate-900 mb-4">
                                    {foundContent?.found ? 'Extracted Content:' : 'Section Content:'}
                                </h4>
                                <div className="text-slate-800 leading-relaxed space-y-2">
                                    {displayContent && (
                                        <p className="whitespace-pre-wrap">
                                            {formatReferenceContent(displayContent, 1000)}
                                        </p>
                                    )}
                                    {foundContent?.context && foundContent.context !== displayContent && (
                                        <div className="mt-4 pt-4 border-t border-slate-200">
                                            <p className="text-sm font-medium text-slate-600 mb-2">Additional Context:</p>
                                            <p className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 p-3 rounded">
                                                {formatReferenceContent(foundContent.context, 800)}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                                <AlertCircle className="h-8 w-8 text-slate-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-slate-900 mb-2">
                                Content Not Found
                            </h3>
                            <p className="text-slate-500 max-w-md mx-auto">
                                The specific section "{sectionReference}" could not be located in the document text.
                                This may be due to formatting differences or the section being referenced indirectly.
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end pt-4 border-t border-slate-100">
                    <Button variant="secondary" onClick={onClose}>
                        Close
                    </Button>
                </div>
            </div>
        </Modal>
    );
};
